import { FormEvent, useEffect, useId, useState } from 'react';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { errorText, fmtDateTime, rpc } from './api';
import { TierPicker } from './bits';
import { SchoolCtx, SupportGate } from './SchoolPage';
import { SchoolOptionsPanels } from './R2Admin';
import { CrossSchoolPanel } from './R3Admin';
import { contactMismatches } from '../../lib/contactCheck';
import { ReviewMark } from './ReviewMark';

// Settings tab (in support mode): school profile, policies, the safety-net
// message students see on every check-in screen, and the manual checklist
// confirmations. The school code is never edited.
export function SettingsTab(ctx: SchoolCtx) {
  const { t } = useI18n();
  return (
    <SupportGate ctx={ctx} what={t['pa.whatSettings']}>
      <ProfilePanel {...ctx} />
      <SafetyNetPanel {...ctx} />
      <div className="two-col two-col-even">
        <PoliciesPanel {...ctx} />
        <StepsPanel {...ctx} />
      </div>
      <HelpLinksPanel {...ctx} />
      <SchoolOptionsPanels {...ctx} />
      <CrossSchoolPanel {...ctx} />
    </SupportGate>
  );
}

function useSaver(ctx: SchoolCtx) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save(fn: string, args: Record<string, unknown>) {
    setBusy(true);
    setError(null);
    const r = await rpc<{ state?: string } | null>(fn, args);
    setBusy(false);
    if (r.error) {
      setError(errorText(t, r.error));
      return false;
    }
    announce(r.data && r.data.state === 'live' && ctx.detail.school.state !== 'live' ? t['pa.nowLive'] : t['pa.saved']);
    await ctx.reload();
    return true;
  }
  return { busy, error, save };
}

function ProfilePanel(ctx: SchoolCtx) {
  const { t } = useI18n();
  const id = useId();
  const s = ctx.detail.school;
  const [name, setName] = useState(s.name);
  const [muni, setMuni] = useState(s.municipality_code ?? '');
  const [num, setNum] = useState(s.official_number ?? '');
  const [lang, setLang] = useState(s.default_language);
  const [tier, setTier] = useState(s.tier ?? '');
  const [munis, setMunis] = useState<{ code: string; name: string }[]>([]);
  const { busy, error, save } = useSaver(ctx);

  useEffect(() => {
    rpc<{ code: string; name: string }[]>('platform_municipalities').then((r) => r.data && setMunis(r.data));
  }, []);

  function submit(e: FormEvent) {
    e.preventDefault();
    save('platform_update_school', {
      p_school_id: s.id,
      p_name: name,
      p_municipality: muni || null,
      p_official_number: num || null,
      p_stages: s.stages,
      p_default_language: lang,
      p_tier: s.school_type === 'customer' ? tier : null,
    });
  }

  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>
        {t['pa.profile']}
      </h2>
      <form className="form form-narrow" onSubmit={submit} noValidate>
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-n`}>
            {t['admin.schoolName']}
          </label>
          <input id={`${id}-n`} className="field" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field-group">
          <span className="field-label" id={`${id}-cl`}>
            {t['admin.schoolCode']}
          </span>
          <p aria-labelledby={`${id}-cl`}>
            <span className="code-chip">{s.code}</span> <span className="form-hint">{t['pa.codeNeverChanges']}</span>
          </p>
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-m`}>
            {t['pa.municipality']}
          </label>
          <span className="select-wrap">
            <select id={`${id}-m`} className="select" value={muni} onChange={(e) => setMuni(e.target.value)}>
              <option value="">{t['pa.chooseMunicipality']}</option>
              {munis.map((m) => (
                <option key={m.code} value={m.code}>
                  {m.name}
                </option>
              ))}
            </select>
          </span>
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-o`}>
            {t['pa.officialNumber']}
          </label>
          <input id={`${id}-o`} className="field" inputMode="numeric" value={num} onChange={(e) => setNum(e.target.value)} />
        </div>
        <fieldset className="inline-radios">
          <legend className="field-label">{t['pa.defaultLanguage']}</legend>
          {(['fi', 'sv', 'en'] as const).map((l) => (
            <label key={l}>
              <input type="radio" name={`${id}-l`} checked={lang === l} onChange={() => setLang(l)} /> {t[`pa.lang_${l}`]}
            </label>
          ))}
        </fieldset>
        {s.school_type === 'customer' && <TierPicker value={tier} onChange={setTier} />}
        <p className="form-hint">{t['pa.levelsOnClasses']}</p>
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {t['pa.save']}
          </button>
        </div>
      </form>
    </section>
  );
}

function SafetyNetPanel(ctx: SchoolCtx) {
  const { t, lang: uiLang } = useI18n();
  const id = useId();
  const s = ctx.detail.school;
  const [fi, setFi] = useState(s.safety_net.fi ?? '');
  const [sv, setSv] = useState(s.safety_net.sv ?? '');
  const [en, setEn] = useState(s.safety_net.en ?? '');
  const [preview, setPreview] = useState<'fi' | 'sv' | 'en'>(uiLang);
  const { busy, error, save } = useSaver(ctx);
  const vals = { fi, sv, en };
  const setters = { fi: setFi, sv: setSv, en: setEn };
  const reviewed = s.safety_net_reviewed ?? {};
  const mismatches = contactMismatches(vals);

  function submit(e: FormEvent) {
    e.preventDefault();
    save('platform_set_safety_net', { p_school_id: s.id, p_fi: fi, p_sv: sv, p_en: en });
  }

  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>
        {t['pa.ckSafetyNet']}
      </h2>
      <p className="form-hint">{t['pa.safetyNetHint']}</p>
      <form className="form" onSubmit={submit} noValidate>
        <div className="safety-grid">
          {(['fi', 'sv', 'en'] as const).map((l) => (
            <div className="field-group" key={l}>
              <label className="field-label" htmlFor={`${id}-${l}`}>
                {t['pa.messageIn'](t[`pa.lang_${l}`])}
              </label>
              <textarea
                id={`${id}-${l}`}
                className="field"
                rows={4}
                maxLength={600}
                value={vals[l]}
                onChange={(e) => setters[l](e.target.value)}
                lang={l}
              />
              {!vals[l].trim() && (
                <button type="button" className="link-btn" onClick={() => setters[l](t[`pa.safetyTemplate_${l}`])}>
                  {t['pa.useTemplate']}
                </button>
              )}
              {(s.safety_net[l] ?? '').trim() !== '' && (
                <ReviewMark ctx={ctx} lang={l} reviewed={!!reviewed[l]} changed={vals[l].trim() !== (s.safety_net[l] ?? '').trim()} />
              )}
            </div>
          ))}
        </div>
        <fieldset className="inline-radios">
          <legend className="field-label">{t['pa.previewIn']}</legend>
          {(['fi', 'sv', 'en'] as const).map((l) => (
            <label key={l}>
              <input type="radio" name={`${id}-p`} checked={preview === l} onChange={() => setPreview(l)} /> {t[`pa.lang_${l}`]}
            </label>
          ))}
        </fieldset>
        {mismatches.length > 0 && (
          <div className="notice notice-block" role="status">
            <p><strong>{t['r4.sn.mismatchTitle']}</strong></p>
            <p>{t['r4.sn.mismatch'](mismatches.join(', '))}</p>
          </div>
        )}
        <div className="safety-preview" aria-label={t['pa.previewLabel']} role="figure">
          <div className="sx-safety" lang={preview}>
            <span className="icon" aria-hidden="true">
              💛
            </span>
            <p>{vals[preview].trim() || t['pa.previewEmpty']}</p>
          </div>
        </div>
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {t['pa.save']}
          </button>
        </div>
      </form>
    </section>
  );
}

function PoliciesPanel(ctx: SchoolCtx) {
  const { t } = useI18n();
  const id = useId();
  const s = ctx.detail.school;
  const [selfService, setSelfService] = useState(s.teacher_self_service);
  const [wholeSchool, setWholeSchool] = useState(s.counsellor_whole_school);
  const { busy, error, save } = useSaver(ctx);

  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>
        {t['pa.policies']}
      </h2>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          save('platform_set_policies', {
            p_school_id: s.id,
            p_teacher_self_service: selfService,
            p_counsellor_whole_school: wholeSchool,
          });
        }}
      >
        <label className="switch">
          <input type="checkbox" role="switch" checked={selfService} onChange={(e) => setSelfService(e.target.checked)} />
          <span>
            <strong>{t['pa.polSelfService']}</strong>
            <span className="choice-desc">{t['pa.polSelfServiceDesc']}</span>
          </span>
        </label>
        <label className="switch">
          <input type="checkbox" role="switch" checked={wholeSchool} onChange={(e) => setWholeSchool(e.target.checked)} />
          <span>
            <strong>{t['pa.polWholeSchool']}</strong>
            <span className="choice-desc">{t['pa.polWholeSchoolDesc']}</span>
          </span>
        </label>
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {t['pa.save']}
          </button>
        </div>
      </form>
    </section>
  );
}

// Help links (0022/0023): one Wilma address and one helpline (name +
// contact), shared across languages — platform admin edits them in support
// mode, same as the safety net, until the school admin has their own
// screens. Both are warning-level checklist items, never required (D12):
// a link the school hasn't set is simply not shown on the check-in's help
// strip.
function HelpLinksPanel(ctx: SchoolCtx) {
  const { t } = useI18n();
  const id = useId();
  const s = ctx.detail.school;
  const [wilma, setWilma] = useState(s.wilma_url ?? '');
  const [name, setName] = useState(s.helpline_name ?? '');
  const [contact, setContact] = useState(s.helpline_contact ?? '');
  const { busy, error, save } = useSaver(ctx);

  function submit(e: FormEvent) {
    e.preventDefault();
    save('platform_set_help_links', { p_school_id: s.id, p_wilma_url: wilma || null, p_helpline_name: name || null, p_helpline_contact: contact || null });
  }

  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>
        {t['r4c.help.title']}
      </h2>
      <p className="form-hint">{t['r4c.help.hint']}</p>
      <form className="form form-narrow" onSubmit={submit} noValidate>
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-w`}>
            {t['r4c.help.wilma']}
          </label>
          <input id={`${id}-w`} className="field" type="url" maxLength={300} value={wilma} onChange={(e) => setWilma(e.target.value)} />
          <p className="form-hint">{t['r4c.help.wilmaHint']}</p>
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-n`}>
            {t['r4c.help.name']}
          </label>
          <input id={`${id}-n`} className="field" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-c`}>
            {t['r4c.help.contact']}
          </label>
          <input id={`${id}-c`} className="field" maxLength={300} value={contact} onChange={(e) => setContact(e.target.value)} />
          <p className="form-hint">{t['r4c.help.contactHint']}</p>
        </div>
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {t['pa.save']}
          </button>
        </div>
      </form>
    </section>
  );
}

function StepsPanel(ctx: SchoolCtx) {
  const { t, locale } = useI18n();
  const id = useId();
  const s = ctx.detail.school;
  const { busy, error, save } = useSaver(ctx);
  const steps: { key: 'profile_confirmed' | 'notice_sent' | 'dpo_signoff'; label: string }[] = [
    { key: 'profile_confirmed', label: t['pa.ckProfile'] },
    { key: 'notice_sent', label: t['pa.ckNotice'] },
    { key: 'dpo_signoff', label: t['pa.ckDpo'] },
  ];
  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>
        {t['pa.confirmations']}
      </h2>
      <p className="form-hint">{t['pa.confirmationsHint']}</p>
      <ul className="school-list">
        {steps.map((st) => {
          const done = s.setup_steps[st.key];
          return (
            <li key={st.key}>
              <span>
                {st.label}
                {done && <span className="text-muted"> · {fmtDateTime(locale, done.at)}</span>}
              </span>
              <span className="pill-row">
                {done ? <Pill kind="good">{t['pa.done']}</Pill> : <Pill kind="warn">{t['pa.notDone']}</Pill>}
                <button
                  type="button"
                  className="link-btn"
                  disabled={busy}
                  onClick={() => save('platform_set_setup_step', { p_school_id: s.id, p_step: st.key, p_done: !done, p_note: null })}
                  aria-label={`${done ? t['pa.reopen'] : t['pa.markDone']}: ${st.label}`}
                >
                  {done ? t['pa.reopen'] : t['pa.markDone']}
                </button>
              </span>
            </li>
          );
        })}
      </ul>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
